export const USPS = [
  "Uncompromised quality",
  "Full customization, no catalogue",
  "Honest pricing",
  "Fast sample turnaround",
  "Senior-led team",
  "Direct-to-architect",
];

// ================ Recent Work — real projects ================
export const RECENT_WORK = [
  {
    id: "multi-aggregate-oval",
    title: "Multi-aggregate Oval",
    category: "Custom Terrazzo Tabletop",
    meta: "White base · Green, mustard, ruby marble chips",
    image: "/home/recent-01-multi-oval.jpg",
  },
  {
    id: "golden-fleck-round",
    title: "Golden Fleck Round",
    category: "Custom Terrazzo Tabletop",
    meta: "Warm-white base · Amber aggregate · Column base",
    image: "/home/recent-02-golden-fleck.jpg",
  },
  {
    id: "emerald-inlay",
    title: "Emerald Inlay",
    category: "Custom Terrazzo Tabletop",
    meta: "Brass-free inlay · Emerald pigment channels",
    image: "/home/recent-03-emerald-inlay.jpg",
  },
  {
    id: "cafe-floor",
    title: "Cafe Terrazzo Floor",
    category: "Cast-on-site Terrazzo",
    meta: "Amber-flecked cream slab · Continuous pour",
    image: "/home/recent-04-cafe-floor.jpg",
  },
  {
    id: "pastel-composition",
    title: "Pastel Composition",
    category: "Custom Terrazzo Tabletop",
    meta: "Peach · Powder blue · Butter · Rose · Painted patches",
    image: "/home/recent-05-pastel.jpg",
  },
];

// ================ Full /work page — categorised gallery ================
const range = (n) => Array.from({ length: n }, (_, i) => i + 1);

// Reshuffles applied per client review:
// - Table Tops #3 (emerald-inlay) and #6 (tabletop-02) moved to Terrazzo Precast
// - Terrazzo Precast #1 (cafe-floor) removed
// - Cement Bricks #7 (brick-07) removed
// - "Other Terrazzo" renamed to "Terrazzo Precast"

const _emeraldInlay = RECENT_WORK.find((w) => w.id === "emerald-inlay");

export const WORK_GROUPS = [
  {
    id: "tabletops",
    label: "Table Tops",
    tagline: "Custom terrazzo tabletops — round, oval, freeform",
    items: [
      { image: RECENT_WORK[0].image, label: RECENT_WORK[0].title }, // multi-aggregate-oval
      { image: RECENT_WORK[1].image, label: RECENT_WORK[1].title }, // golden-fleck-round
      // #3 emerald-inlay moved out
      { image: RECENT_WORK[4].image, label: RECENT_WORK[4].title }, // pastel-composition
      { image: "/work/tabletop-01.jpg", label: "Tabletop 01" },
      // #6 tabletop-02 moved out
      { image: "/work/tabletop-03.jpg", label: "Tabletop 03" },
      { image: "/work/tabletop-04.jpg", label: "Tabletop 04" },
      { image: "/work/tabletop-05.jpg", label: "Tabletop 05" },
      { image: "/work/tabletop-06.jpg", label: "Tabletop 06" },
      { image: "/work/tabletop-07.jpg", label: "Tabletop 07" },
    ],
  },
  {
    id: "terrazzo",
    label: "Terrazzo Precast",
    tagline: "Floors, walls, slabs, cast-on-site pours & installations",
    items: [
      // Reshuffled from Table Tops
      { image: _emeraldInlay.image, label: _emeraldInlay.title },
      { image: "/work/tabletop-02.jpg", label: "Precast piece 01" },
      ...range(30).map((i) => ({
        image: `/work/terrazzo-${String(i).padStart(2, "0")}.jpg`,
        label: `Precast project ${String(i).padStart(2, "0")}`,
      })),
    ],
  },
  {
    id: "bricks",
    label: "Cement Bricks",
    tagline: "Cladding brick installations — walls, facades, feature interiors",
    items: range(6).map((i) => ({
      image: `/work/brick-${String(i).padStart(2, "0")}.jpg`,
      label: `Brick project ${String(i).padStart(2, "0")}`,
    })),
  },
];

const P = "https://www.parmandesigns.com/assets/img/terrazzo";

// All 70 samples flattened into a single catalogue, renamed TI-01 .. TI-70
const _slugCount = [
  ["series-a", 28],
  ["series-b", 34],
  ["series-c", 2],
  ["series-d", 6],
];
const _buildCatalogue = () => {
  const items = [];
  let idx = 1;
  for (const [slug, count] of _slugCount) {
    for (let i = 1; i <= count; i++) {
      items.push({
        code: `TI-${String(idx).padStart(2, "0")}`,
        image: `${P}/${slug}/${slug}-${i}.jpg`,
      });
      idx += 1;
    }
  }
  return items;
};

export const TERRAZZO_CATALOGUE = _buildCatalogue();

// ================ Cement Bricks (Cladding) Catalogue ================
export const BRICKS_CATALOGUE = [
  {
    id: "plain",
    label: "Plain Series",
    tagline: "Clean-faced cladding bricks",
    dimensions: "200 × 60 × 14 mm",
    packing: "50 pcs / box",
    items: [
      { code: "001", name: "Plain Red",         image: "/bricks/plain-red.jpg" },
      { code: "002", name: "Plain Grey",        image: "/bricks/plain-grey.jpg" },
      { code: "003", name: "Plain White",       image: "/bricks/plain-white.jpg" },
      { code: "004", name: "Plain Burnt Red",   image: "/bricks/plain-burnt-red.jpg" },
      { code: "005", name: "Plain Burnt White", image: "/bricks/plain-burnt-white.jpg" },
      { code: "—",   name: "Plain Combination", image: "/bricks/plain-combination.jpg" },
    ],
  },
  {
    id: "rustic",
    label: "Rustic Series",
    tagline: "Hand-textured cladding bricks",
    dimensions: "210 × 63 × 14 mm",
    packing: "48 pcs / box",
    items: [
      { code: "101", name: "Rustic Red",         image: "/bricks/rustic-red.jpg" },
      { code: "102", name: "Rustic Grey",        image: "/bricks/rustic-grey.jpg" },
      { code: "103", name: "Rustic White",       image: "/bricks/rustic-white.jpg" },
      { code: "104", name: "Rustic Burnt Red",   image: "/bricks/rustic-burnt-red.jpg" },
      { code: "105", name: "Rustic Burnt White", image: "/bricks/rustic-burnt-white.jpg" },
      { code: "—",   name: "Rustic Combination", image: "/bricks/rustic-combination.jpg" },
    ],
  },
];

export const BRICKS_SPECS = [
  ["Compressive Strength", "20.44 N/mm² · 208.40 Kg/cm²"],
  ["Density",              "1795 Kg/m³"],
  ["Water Absorption",     "Below 10%"],
  ["Efflorescence",        "Nil"],
];

export const FINISHES = [
  {
    id: "polished",
    name: "Polished finish",
    image: `${P}/polished.jpg`,
    uses: [
      ["Interior Flooring", "Yes"], ["Exterior Flooring", "Yes"],
      ["Interior Walls", "Yes"], ["Exterior Walls", "Yes"],
      ["Interior Countertops", "Yes"], ["Exterior Countertops", "Yes"],
      ["Interior Stairs", "Yes"], ["Exterior Stairs", "Yes"],
      ["Interior Base Tile", "Yes"], ["Interior Wet Areas", "Yes"],
      ["Pool Coping / Wall Cap", "Yes"], ["Poured Toppings", "Yes"],
    ],
  },
  {
    id: "matt",
    name: "Matt finish",
    image: `${P}/matt.jpg`,
    uses: [
      ["Interior Flooring", "Yes"], ["Exterior Flooring", "Yes"],
      ["Interior Walls", "Yes"], ["Exterior Walls", "Yes"],
      ["Interior Countertops", "Yes"], ["Exterior Countertops", "Yes"],
      ["Interior Stairs", "Yes"], ["Exterior Stairs", "Yes"],
      ["Interior Base Tile", "Yes"], ["Interior Wet Areas", "Yes"],
      ["Pool Coping / Wall Cap", "Yes"], ["Poured Toppings", "Yes"],
    ],
  },
  {
    id: "leather",
    name: "Leather finish",
    image: `${P}/leather.jpg`,
    uses: [
      ["Interior Flooring", "N/A"], ["Exterior Flooring", "Yes"],
      ["Interior Walls", "Yes"], ["Exterior Walls", "Yes"],
      ["Interior Countertops", "N/A"], ["Exterior Countertops", "N/A"],
      ["Interior Stairs", "N/A"], ["Exterior Stairs", "N/A"],
      ["Interior Base Tile", "N/A"], ["Interior Wet Areas", "N/A"],
      ["Pool Coping / Wall Cap", "Yes"], ["Poured Toppings", "Yes"],
    ],
  },
];

// Legacy — kept for Home page sections
export const PRODUCTS = [
  {
    id: "tabletops",
    number: "01",
    name: "Custom Terrazzo Tabletops",
    short_description: "Bespoke tabletops fabricated to any shape, size, or aggregate composition.",
    image: "https://images.pexels.com/photos/10410101/pexels-photo-10410101.jpeg",
  },
  {
    id: "slabs",
    number: "02",
    name: "Precast Terrazzo Slabs",
    short_description: "Premium precast slabs for floors, walls and commercial surfaces.",
    image: "https://images.pexels.com/photos/4977440/pexels-photo-4977440.jpeg",
  },
  {
    id: "flooring",
    number: "03",
    name: "Cast-on-site Terrazzo Flooring",
    short_description: "Seamless, joint-free monolithic floors poured on location.",
    image: "https://images.unsplash.com/photo-1558346648-9757f2fa4474",
  },
];

export const PRINCIPLES = [
  { n: "01", title: "No middlemen",
    body: "We sell what we make. The senior who answers your call is the same one walking the studio floor." },
  { n: "02", title: "No catalogue",
    body: "Every job is custom. Shape, size, aggregate, finish — we will engineer it to your drawing." },
  { n: "03", title: "Honest pricing",
    body: "A single quote, sent quickly, with the work broken down. No padding for a sales chain that does not exist." },
  { n: "04", title: "Samples in days",
    body: "The studio runs sample pours on a tight cadence. You will see and touch the finish before you commit." },
];

export const PROJECT_TYPES = [
  "Precast Terrazzo",
  "Cast-on-site Terrazzo",
  "Cement Cladding Bricks",
  "Mixed / Multiple",
  "Trade enquiry",
];

// ================ Why Choose Terrazzo India ================
export const WHY_CHOOSE = {
  eyebrow: "What Makes Us Different",
  headline: "Two things we always keep in mind — Versatility. Value.",
  copy: "Our surfaces work across every setting and every scale. And we never compromise on quality to hit a price — we've built a way to deliver both, which is rare and exactly what makes us different.",
  team: {
    eyebrow: "The People Behind the Product",
    body: "Behind every project is a team that has seen it all. Experienced, senior professionals who understand architectural and design language without needing it explained twice. Brief us once, we get it. No back and forth. No middlemen interpreting your vision. Just the right people, moving fast and delivering exactly what was discussed.",
  },
  serve: {
    eyebrow: "Who We Serve",
    items: [
      "Architects who are tired of following up",
      "Designers who need a supplier that speaks their language",
      "End users who want something beautiful, lasting, and worth every rupee",
    ],
  },
  usp: {
    eyebrow: "Our USP",
    items: [
      "Uncompromised quality",
      "Full customisation",
      "Honest pricing",
      "Fast samples",
      "Experienced team",
      "Direct service",
    ],
    closer: "When you call Terrazzo India, you get Terrazzo India — the ones who cast it, deliver it, and stand behind every square metre. No agents. No brokers. No surprises.",
  },
};

export const CONTACT = {
  phone: "+91 98201 68800",
  phoneHref: "tel:+919820168800",
  email: "terrazzoindia21@gmail.com",
  address: [
    "Yogii Industries (Terrazzo India),",
    "E D Industrial Estate,",
    "Saagpada, Chinchoti,",
    "Near Bhajan Lal Dairy,",
    "Vasai East, Maharashtra — 421302.",
  ],
  hours: "Mon — Sat · 9:30 to 19:00 IST",
  instagram: "https://www.instagram.com/terrazzo_india/",
  instagramHandle: "@terrazzo_india",
};
